// frontend/src/pages/AnalysisPage/sections/Calculations/Calculations.js
// Purpose: Handle calculations functionality in the analysis workflow
// Desired outcome: Allow users to run calculations with different scenarios and display results correctly, particularly fixing CO₂e (carbon footprint) calculations

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFile,
  selectCalculations,
  selectCalculationsLoading,
  selectCalculationsError,
  checkCalculationReadiness,
  runCalculations,
  confirmCalculations,
  getCalculationResults
} from '../../../../state/analysis/analysisSlice';
import './Calculations.css';

const Calculations = () => {
  const dispatch = useDispatch();
  const currentFile = useSelector(selectCurrentFile);
  const calculations = useSelector(selectCalculations);
  const loading = useSelector(selectCalculationsLoading);
  const error = useSelector(selectCalculationsError);

  const [selectedScenarios, setSelectedScenarios] = useState(['scenario_A', 'scenario_C']); // Default to including carbon footprint
  const [readinessCheck, setReadinessCheck] = useState(null);
  const [calculationsRun, setCalculationsRun] = useState(false);

  // Add refs to track API call status and prevent recursion
  const readinessCheckRequested = useRef(false);
  const calculationsRequested = useRef(false);

  // Check if file is ready for calculations - using ref to prevent recursion
  useEffect(() => {
    if (currentFile?.fileID && !readinessCheckRequested.current) {
      // Mark as requested to prevent multiple calls
      readinessCheckRequested.current = true;

      dispatch(checkCalculationReadiness(currentFile.fileID))
        .unwrap()
        .then(result => {
          setReadinessCheck(result);
          // No need to reset the ref as we don't want to call this again until user action
        })
        .catch(err => {
          console.error('Error checking calculation readiness:', err);
        });
    }
  }, [dispatch, currentFile]);

  // Load existing calculation results if available - using ref to prevent recursion
  useEffect(() => {
    if (currentFile?.fileID && !calculations.data && !loading && !calculationsRequested.current) {
      // Mark as requested to prevent multiple calls
      calculationsRequested.current = true;

      dispatch(getCalculationResults(currentFile.fileID))
        .unwrap()
        .then(results => {
          if (results && Object.keys(results).length > 0 && results.parts && results.parts.length > 0) {
            setCalculationsRun(true);
          } else {
            // Reset the flag if no valid calculations exist yet
            calculationsRequested.current = false;
          }
        })
        .catch(() => {
          // Reset the flag if error occurs
          calculationsRequested.current = false;
        });
    }
  }, [dispatch, currentFile, calculations.data, loading]);

  // Handle scenario selection
  const handleScenarioChange = (scenario) => {
    const index = selectedScenarios.indexOf(scenario);
    if (index === -1) {
      setSelectedScenarios([...selectedScenarios, scenario]);
    } else {
      setSelectedScenarios(selectedScenarios.filter(s => s !== scenario));
    }
  };

  // Run calculations
  const handleRunCalculations = () => {
    if (currentFile?.fileID) {
      // Reset the calculation requested flag to allow a new calculation
      calculationsRequested.current = true;

      dispatch(runCalculations({
        fileID: currentFile.fileID,
        selectedScenarios
      }))
        .unwrap()
        .then(() => {
          setCalculationsRun(true);
        })
        .catch(err => {
          console.error('Error running calculations:', err);
          // Reset flag on error to allow retry
          calculationsRequested.current = false;
        });
    }
  };

  // Confirm calculations and proceed to next step
  const handleConfirmCalculations = () => {
    if (currentFile?.fileID) {
      dispatch(confirmCalculations({
        fileID: currentFile.fileID,
        calculationStatus: 'Completed'
      }))
        .unwrap()
        .then(() => {
          console.log('Calculations confirmed successfully');
        })
        .catch(err => {
          console.error('Error confirming calculations:', err);
        });
    }
  };

  // Function to check readiness again
  const checkReadinessAgain = () => {
    if (currentFile?.fileID) {
      // Reset the flag to allow a new readiness check
      readinessCheckRequested.current = false;

      dispatch(checkCalculationReadiness(currentFile.fileID))
        .unwrap()
        .then(result => {
          setReadinessCheck(result);
        })
        .catch(err => {
          console.error('Error checking calculation readiness:', err);
        });
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Format CO2e values - improved to handle different formats
  const formatCO2e = (value, unit = 'kg/CO2e') => {
    // Handle string values that might be numbers (API inconsistency)
    if (typeof value === 'string') {
      // Try to parse as number if it looks like one
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        return `${numericValue.toFixed(2)} ${unit}`;
      }
      // Return the string as is if it's not numeric
      return value;
    }
    
    // Handle numeric values directly
    if (typeof value === 'number') {
      return `${value.toFixed(2)} ${unit}`;
    }
    
    // Return N/A for undefined, null, or other types
    return 'N/A';
  };

  // Check if calculations can be run
  const canRunCalculations =
    readinessCheck &&
    readinessCheck.ready &&
    selectedScenarios.length > 0 &&
    !loading;

  // Check if calculations can be confirmed
  const canConfirmCalculations =
    calculationsRun &&
    calculations.data &&
    !loading;

  // Helper to safely get CO2e values from calculations
  const getCO2eValue = (part, field) => {
    if (!part?.calculatedValues) return null;
    
    // Try both direct access and parsing numbered string formats
    const value = part.calculatedValues[field]?.value;
    
    if (value === undefined || value === null) return null;
    
    // Return the value (might be numeric or string)
    return value;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="calculations-section">
        <h2>Calculations</h2>
        <div className="loading-indicator">
          <p>Processing calculations...</p>
          <div className="progress-spinner"></div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="calculations-section">
        <h2>Calculations</h2>
        <div className="error-message">
          {error.message || 'An error occurred while processing calculations.'}
        </div>
      </div>
    );
  }

  // Render readiness check results
  if (readinessCheck && !readinessCheck.ready && !calculationsRun) {
    return (
      <div className="calculations-section">
        <h2>Calculations</h2>
        <div className="warning-message">
          <h3>Data Validation Warning</h3>
          <p>{readinessCheck.message || 'Some required data is missing for calculations.'}</p>

          {readinessCheck.partsWithMissingData > 0 && (
            <div className="missing-data-summary">
              <p>
                <strong>{readinessCheck.partsWithMissingData}</strong> out of <strong>{readinessCheck.totalParts}</strong> parts have missing data.
              </p>
              <p>Missing fields: {readinessCheck.missingFields.join(', ')}</p>
            </div>
          )}

          <div className="action-buttons">
            <button className="secondary-button" onClick={checkReadinessAgain}>
              Check Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="calculations-section">
      <h2>Calculations</h2>

      {/* Scenario Selection */}
      <div className="calculation-settings">
        <h3>Select Calculation Scenarios</h3>
        <div className="scenario-options">
          <label className="scenario-option">
            <input
              type="checkbox"
              checked={selectedScenarios.includes('scenario_A')}
              onChange={() => handleScenarioChange('scenario_A')}
            />
            <div className="scenario-details">
              <span className="scenario-name">Lead Time Reduction</span>
              <span className="scenario-description">Calculate potential lead time savings</span>
            </div>
          </label>

          <label className="scenario-option">
            <input
              type="checkbox"
              checked={selectedScenarios.includes('scenario_B')}
              onChange={() => handleScenarioChange('scenario_B')}
            />
            <div className="scenario-details">
              <span className="scenario-name">Cost Savings</span>
              <span className="scenario-description">Calculate potential cost savings</span>
            </div>
          </label>

          <label className="scenario-option">
            <input
              type="checkbox"
              checked={selectedScenarios.includes('scenario_C')}
              onChange={() => handleScenarioChange('scenario_C')}
            />
            <div className="scenario-details">
              <span className="scenario-name">Carbon Footprint Reduction</span>
              <span className="scenario-description">Calculate potential carbon footprint reduction</span>
            </div>
          </label>
        </div>

        <button
          className="primary-button"
          disabled={!canRunCalculations}
          onClick={handleRunCalculations}
        >
          Run Calculations
        </button>
      </div>

      {/* Calculation Results */}
      {calculations.data && calculations.data.parts && calculations.data.parts.length > 0 && (
        <div className="calculation-results">
          <h3>Results</h3>

          {/* Summary Cards */}
          <div className="summary-cards">
            {calculations.data.summary && calculations.data.summary.scenario_A && selectedScenarios.includes('scenario_A') && (
              <div className="summary-card">
                <h4>Lead Time Reduction</h4>
                <div className="summary-value">
                  {calculations.data.summary.scenario_A.totalLeadTimeReduction.value.toFixed(1)} days
                </div>
                <div className="summary-subtext">
                  Average: {calculations.data.summary.scenario_A.averageLeadTimeReduction.value.toFixed(1)} days per part
                </div>
              </div>
            )}

            {calculations.data.summary && calculations.data.summary.scenario_B && selectedScenarios.includes('scenario_B') && (
              <div className="summary-card">
                <h4>Cost Savings</h4>
                <div className="summary-value">
                  {formatCurrency(calculations.data.summary.scenario_B.totalCostSavings.value)}
                </div>
                <div className="summary-subtext">
                  Average: {formatCurrency(calculations.data.summary.scenario_B.averageCostSavings.value)} per part
                </div>
              </div>
            )}

            {calculations.data.summary && calculations.data.summary.scenario_C && selectedScenarios.includes('scenario_C') && (
              <div className="summary-card">
                <h4>Carbon Footprint Reduction</h4>
                <div className="summary-value">
                  {calculations.data.summary.scenario_C.totalCurrentCO2e && 
                   calculations.data.summary.scenario_C.totalProjectedCO2e ? 
                   `${(parseFloat(calculations.data.summary.scenario_C.totalCurrentCO2e) - 
                   parseFloat(calculations.data.summary.scenario_C.totalProjectedCO2e)).toFixed(2)} kg/CO2e` : 
                   'N/A'}
                </div>
                <div className="summary-subtext">
                  {calculations.data.summary.scenario_C.totalCurrentCO2e ? 
                   `Current: ${parseFloat(calculations.data.summary.scenario_C.totalCurrentCO2e).toFixed(2)} kg/CO2e` : ''}
                  {calculations.data.summary.scenario_C.totalProjectedCO2e ? 
                   ` • Projected: ${parseFloat(calculations.data.summary.scenario_C.totalProjectedCO2e).toFixed(2)} kg/CO2e` : ''}
                </div>
              </div>
            )}
          </div>

          {/* Part Details Table */}
          <div className="parts-table-container">
            <h4>Part Details</h4>
            <table className="parts-table">
              <thead>
                <tr>
                  <th>Part Name</th>
                  <th>Category</th>
                  {selectedScenarios.includes('scenario_A') && (
                    <>
                      <th>Lead Time Reduction</th>
                      <th>Lead Time Reduction %</th>
                    </>
                  )}
                  {selectedScenarios.includes('scenario_B') && (
                    <>
                      <th>Cost Savings</th>
                      <th>Cost Savings %</th>
                    </>
                  )}
                  {selectedScenarios.includes('scenario_C') && (
                    <>
                      <th>CO2e Footprint Reduction</th>
                      <th>Lifetime CO2e Savings</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {calculations.data.parts && calculations.data.parts.map(part => (
                  <tr key={part.partID}>
                    <td>{part.originalName}</td>
                    <td>{part.matchedCategory}</td>

                    {selectedScenarios.includes('scenario_A') && (
                      <>
                        <td>
                          {part.calculatedValues && part.calculatedValues.scenario_A_leadTimeReduction ?
                            `${part.calculatedValues.scenario_A_leadTimeReduction.value.toFixed(1)} days` :
                            'N/A'}
                        </td>
                        <td>
                          {part.calculatedValues && part.calculatedValues.scenario_A_leadTimePercentage ?
                            `${part.calculatedValues.scenario_A_leadTimePercentage.value.toFixed(1)}%` :
                            'N/A'}
                        </td>
                      </>
                    )}

                    {selectedScenarios.includes('scenario_B') && (
                      <>
                        <td>
                          {part.calculatedValues && part.calculatedValues.scenario_B_costSavings ?
                            formatCurrency(part.calculatedValues.scenario_B_costSavings.value) :
                            'N/A'}
                        </td>
                        <td>
                          {part.calculatedValues && part.calculatedValues.scenario_B_costPercentage ?
                            `${part.calculatedValues.scenario_B_costPercentage.value.toFixed(1)}%` :
                            'N/A'}
                        </td>
                      </>
                    )}

                    {selectedScenarios.includes('scenario_C') && (
                      <>
                        <td>
                          {(() => {
                            // Get CO2e production savings value
                            const prodSavings = getCO2eValue(part, 'scenario_C_co2e_production_savings');
                            
                            // If we have a valid value, format it
                            if (prodSavings !== null) {
                              return formatCO2e(prodSavings);
                            }
                            
                            // Try alternative fields if standard one isn't available
                            const curCO2e = getCO2eValue(part, 'scenario_C_KD_curCO2e');
                            const projCO2e = getCO2eValue(part, 'scenario_C_KD_projCO2e');
                            
                            if (curCO2e !== null && projCO2e !== null) {
                              // Calculate difference if we have both values
                              const numCurCO2e = typeof curCO2e === 'string' ? parseFloat(curCO2e) : curCO2e;
                              const numProjCO2e = typeof projCO2e === 'string' ? parseFloat(projCO2e) : projCO2e;
                              
                              if (!isNaN(numCurCO2e) && !isNaN(numProjCO2e)) {
                                return formatCO2e(numCurCO2e - numProjCO2e);
                              }
                            }
                            
                            return 'N/A';
                          })()}
                        </td>
                        <td>
                          {(() => {
                            // Get CO2e lifetime savings value
                            const lifetimeSavings = getCO2eValue(part, 'scenario_C_co2e_lifetime_savings');
                            
                            // If we have a valid value, format it
                            if (lifetimeSavings !== null) {
                              return formatCO2e(lifetimeSavings);
                            }
                            
                            return 'N/A';
                          })()}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="action-buttons">
            <button
              className="primary-button"
              disabled={!canConfirmCalculations}
              onClick={handleConfirmCalculations}
            >
              Confirm & Proceed to Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculations;